import { useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { downloadResumePdf, downloadRirekishoPdf } from '@src/api/core/unifiedPdfDownloader';
import { FeatureFlag, useFeatureFlag } from '@src/config/featureFlags';
import store from '@src/store/index';
import { selectFocusMode } from '@src/store/slices/focus_mode';
import { updateRefreshPdf } from '@src/store/slices/ui';
import { useCallback, useEffect, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import { useFocusModeSync } from './useFocusModeSync';
import { useKeyboardNavigation } from './useKeyboardNavigation';
import { usePdfNavigation } from './usePdfNavigation';
import { usePdfRendering } from './usePdfRendering';
import { usePdfState } from './usePdfState';
import { useScreenSizeSync } from './useScreenSizeSync';

type UsePdfPreviewProps = {
  onPageChange?: (page: number) => void;
  onPageTotalChange?: (total: number) => void;
  page?: number;
  onPdfScrollingChange?: (isScrolling: boolean) => void;
  onRenderingChange?: (isRendering: boolean) => void;
};

export const usePdfPreview = ({
  onPageChange,
  onPageTotalChange,
  page: controlledPage,
  onPdfScrollingChange,
  onRenderingChange,
}: UsePdfPreviewProps) => {
  // Theme and responsive
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
  const isTablet = useMediaQuery(theme.breakpoints.between('sm', 'md'));
  const isRirekishoEnabled = useFeatureFlag(FeatureFlag.USE_RIREKISHO);

  // Route
  const location = useLocation();
  const isRirekishoMode = isRirekishoEnabled && location.pathname.startsWith('/rirekisho');

  // Refs & State
  const canvasRef = useRef<HTMLCanvasElement>(null);
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [isVisible, _setIsVisible] = useState<boolean>(true);

  // Redux
  const isFocusMode = useSelector(selectFocusMode);

  // Sub-hooks
  const { token, pdfUrl, isInitialState } = usePdfState(isRirekishoMode);

  const { page, handlePageChange } = usePdfNavigation(
    onPageChange,
    scrollContainerRef,
    controlledPage
  );

  const {
    pageTotal,
    isRendering,
    pdfError,
    lastRenderParamsRef,
    resetCanvas,
    setIsRendering,
    setPageTotal,
    setPdfError,
  } = usePdfRendering(canvasRef, pdfUrl, page, isFocusMode, onPageTotalChange);

  useScreenSizeSync(
    isMobile,
    isTablet,
    canvasRef,
    page,
    isRendering,
    isRirekishoMode,
    lastRenderParamsRef,
    setIsRendering,
    setPageTotal,
    setPdfError,
    onPageTotalChange
  );

  useFocusModeSync(isFocusMode, isRirekishoMode, token, resetCanvas);

  useKeyboardNavigation(scrollContainerRef, page, pageTotal, handlePageChange);

  // Effects & Handlers
  useEffect(() => {
    if (onRenderingChange) {
      onRenderingChange(isRendering);
    }
  }, [isRendering, onRenderingChange]);

  useEffect(() => {
    const container = scrollContainerRef.current;
    if (!container || !onPdfScrollingChange) {
      return;
    }

    let scrollTimeout: NodeJS.Timeout | null = null;

    const handleScroll = (): void => {
      onPdfScrollingChange(true);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
      scrollTimeout = setTimeout(() => {
        onPdfScrollingChange(false);
      }, 300);
    };

    container.addEventListener('scroll', handleScroll, { passive: true });
    return () => {
      container.removeEventListener('scroll', handleScroll);
      if (scrollTimeout) {
        clearTimeout(scrollTimeout);
      }
    };
  }, [onPdfScrollingChange]);

  useEffect(() => {
    if (!isFocusMode) {
      // 遅延を最小限に抑えてスケルトン表示時間を短縮（以前は500ms）
      const checkPdfDisplay = setTimeout(() => {
        if (canvasRef.current && pdfUrl) {
          resetCanvas();
        }
      }, 100);
      return () => clearTimeout(checkPdfDisplay);
    }
  }, [isFocusMode, pdfUrl, resetCanvas]);

  const handleRefresh = useCallback((): void => {
    store.dispatch(updateRefreshPdf(true));
  }, []);

  const handleDownloadClick = useCallback((): void => {
    if (!pdfUrl) {
      console.warn('PDFダウンロードURLが取得できませんでした');
      return;
    }
    if (isRirekishoMode) {
      downloadRirekishoPdf(pdfUrl);
    } else {
      downloadResumePdf(pdfUrl);
    }
  }, [pdfUrl, isRirekishoMode]);

  return {
    // Refs
    canvasRef,
    scrollContainerRef,
    // State
    page,
    pageTotal,
    isRendering,
    isFocusMode,
    isInitialState,
    isVisible,
    pdfUrl,
    pdfError,
    isRirekishoMode,
    // Handlers
    handlePageChange,
    handleRefresh,
    handleDownloadClick,
  };
};
